import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { CubeProvider } from '@cubejs-client/react';
import { useQueries, useQuery } from '@tanstack/react-query';
import JSZip from 'jszip';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { H3, LocaleContext, Spinner, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { PageHeader, PageWrapper, ReportingGrid, SidePanelLayout, } from 'libs.react';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { DashboardRefreshWidget } from '../dashboard_refresh_widget';
import { ExportReportModal } from './export_report_modal.component';
import { DataReportGlobalFilters } from './report_dashboard_filters.component';
import { TestEnvironmentSelector } from './test_environment_selector.component';
import { DataReportsContext } from '../../../contexts/data_reports/data_reports.context';
import { sortDashboardChartRefsForGrid, buildDefaultFilterParams } from '../../../dashboards/utils';
import { ReportAnalyticsEvents } from '../../constants/report_analytics_events.constants';
import { useCubeApi } from '../../hooks/use_cube_api';
import { fetchAllCubePages } from '../../hooks/use_paginated_cube_query';
import { useReportAnalytics } from '../../hooks/use_report_analytics';
import { useReportEnvironmentOptions } from '../../hooks/use_report_environment_options';
import { getExportTimestamp, getQueryParams, getTransformedExportData, sanitizeFileName, } from '../../utils/data_reports.utils';
import { ChartCard } from '../report_chart/report_chart_card.component';
import { ChartFilterModal } from '../report_chart/report_chart_filters/chart_filter_modal.component';
const CSV_BOM = '\uFEFF';
const EXPORT_PAGE_LIMIT = 50000;
const convertJsonToCsv = (rows) => {
    if (!rows || rows.length === 0) {
        return '';
    }
    const headers = Object.keys(rows[0]);
    const csvRows = rows.map((row) => headers.map((fieldName) => JSON.stringify(row[fieldName] || '')).join(','));
    return [headers.join(','), ...csvRows].join('\n');
};
export const DataReportDashboard = ({ studyId, reportId, rosettaReports, reportsListPath, }) => {
    const webClient = useApiClient(ApiClientService.WEB);
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const navigate = useNavigate();
    const { cubeApi } = useCubeApi();
    const { selectedTestEnv, activeReportsTab, isInitialized, libRequestsParams, devEnvironments, testEnvironmentsOptions, applyValueFormatting, } = useContext(DataReportsContext);
    const locale = useContext(LocaleContext).toString();
    const toast = useToastNotification();
    const translate = useLocalizeMessage();
    const [searchParams] = useSearchParams();
    const { logReportEvent } = useReportAnalytics(studyId, reportId);
    // Analytics: dashboard load timing
    const dashboardLoadStartRef = useRef(performance.now());
    const [chartStatusMap, setChartStatusMap] = useState({});
    const chartStatusMapRef = useRef(chartStatusMap);
    chartStatusMapRef.current = chartStatusMap;
    const dashboardLoadCompleteLoggedRef = useRef(false);
    const env = searchParams.get('env');
    const rid = searchParams.get('rid');
    const [shouldShowEnvironmentSelector, setShouldShowEnvironmentSelector] = useState(false);
    const { filteredOptions: filteredEnvironmentOptions } = useReportEnvironmentOptions(studyId, reportId, devEnvironments ?? [], testEnvironmentsOptions ?? []);
    const isRidInActiveVersion = useMemo(() => {
        if (!rid) {
            return true;
        }
        if (!rosettaReports?.length) {
            return true;
        }
        return rosettaReports.some((report) => report.libraryResourceId === rid);
    }, [rid, rosettaReports]);
    const [filterParams, setFilterParams] = useState([]);
    const [chartQueryFilters, setChartQueryFilters] = useState({});
    const [chartPresetFilters, setChartPresetFilters] = useState({});
    const [chartFilterModal, setChartFilterModal] = useState({
        id: undefined,
        isOpen: false,
        filterType: undefined,
        filterData: undefined,
    });
    const [nonTableChartResultSetsById, setNonTableChartResultSetsById] = useState({});
    const [isExportLoading, setIsExportLoading] = useState(false);
    const [exportProgress, setExportProgress] = useState({ processed: 0, total: 0 });
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [forceDisablePreAggs, setForceDisablePreAggs] = useState(false);
    const [refreshNonce, setRefreshNonce] = useState(0);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [chartRefreshTimeMap, setChartRefreshTimeMap] = useState({});
    useEffect(() => {
        if (env === 'test' && !selectedTestEnv.value) {
            setShouldShowEnvironmentSelector(true);
        }
        else {
            setShouldShowEnvironmentSelector(false);
        }
    }, [env, selectedTestEnv.value]);
    // Analytics: session start time
    const sessionStartRef = useRef(performance.now());
    // Analytics: fire page viewed, dashboard load started, and session started on mount
    useEffect(() => {
        logReportEvent(ReportAnalyticsEvents.PAGE_VIEWED);
        logReportEvent(ReportAnalyticsEvents.DASHBOARD_LOAD_STARTED);
        logReportEvent(ReportAnalyticsEvents.SESSION_STARTED);
        dashboardLoadStartRef.current = performance.now();
        sessionStartRef.current = performance.now();
        dashboardLoadCompleteLoggedRef.current = false;
        setChartStatusMap({});
        return () => {
            const sessionDurationMs = Math.round(performance.now() - sessionStartRef.current);
            // If dashboard never finished loading, it's an abandonment
            if (!dashboardLoadCompleteLoggedRef.current) {
                const statuses = Object.values(chartStatusMapRef.current);
                logReportEvent(ReportAnalyticsEvents.ABANDONED, {
                    session_duration_ms: sessionDurationMs,
                    charts_loaded: statuses.filter((s) => s === 'loaded').length,
                    charts_total: statuses.length,
                });
            }
            logReportEvent(ReportAnalyticsEvents.SESSION_ENDED, {
                session_duration_ms: sessionDurationMs,
            });
        };
    }, [reportId]);
    // Analytics: handle tab close / navigation away via beforeunload
    useEffect(() => {
        const handleBeforeUnload = () => {
            const sessionDurationMs = Math.round(performance.now() - sessionStartRef.current);
            if (!dashboardLoadCompleteLoggedRef.current) {
                const statuses = Object.values(chartStatusMapRef.current);
                logReportEvent(ReportAnalyticsEvents.ABANDONED, {
                    session_duration_ms: sessionDurationMs,
                    charts_loaded: statuses.filter((s) => s === 'loaded').length,
                    charts_total: statuses.length,
                });
            }
            logReportEvent(ReportAnalyticsEvents.SESSION_ENDED, {
                session_duration_ms: sessionDurationMs,
            });
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [logReportEvent]);
    // Analytics: callback for individual chart load status updates
    const handleChartLoadStatus = useCallback((chartId, status) => {
        setChartStatusMap((prev) => ({ ...prev, [chartId]: status }));
    }, []);
    const handleChartLastRefreshTime = useCallback((chartId, lastRefreshTime) => {
        setChartRefreshTimeMap((prev) => {
            // Bail on an unchanged value: ChartCard's `resultSets` isn't reference-stable across its own
            // renders, so this fires on every render of that chart. Building a new map unconditionally
            // here would re-render this dashboard, which re-renders that ChartCard, which fires this
            // again — an infinite loop (SSP sort-fix investigation, see table_chart.component.tsx).
            if (prev[chartId] === lastRefreshTime) {
                return prev;
            }
            return { ...prev, [chartId]: lastRefreshTime };
        });
    }, []);
    const handleEnvironmentSelected = () => {
        setShouldShowEnvironmentSelector(false);
    };
    const handleNonTableResultSetsChange = useCallback((chartId, resultSets) => {
        setNonTableChartResultSetsById((prev) => {
            if (!resultSets || resultSets.length === 0) {
                if (!(chartId in prev)) {
                    return prev;
                }
                const next = { ...prev };
                delete next[chartId];
                return next;
            }
            return {
                ...prev,
                [chartId]: resultSets,
            };
        });
    }, []);
    const handleRefresh = useCallback(() => {
        logReportEvent(ReportAnalyticsEvents.DATA_REFRESH_CLICKED);
        setIsRefreshing(true);
        setForceDisablePreAggs(true);
        setChartStatusMap({});
        setChartRefreshTimeMap({});
        setRefreshNonce((nonce) => nonce + 1);
        dashboardLoadCompleteLoggedRef.current = false;
        dashboardLoadStartRef.current = performance.now();
    }, [logReportEvent]);
    // Study config reports: fetch specific version from library by ID
    const { data: studyConfigDashboard, isLoading: isLoadingStudyConfig } = useQuery({
        queryKey: ['data_reports', studyId, 'library_dashboard_by_id', rid],
        queryFn: async () => {
            const { data: response } = await libraryClient.get('/v1/reporting_dashboard', {
                params: { id: rid, ...libRequestsParams },
            });
            return response.data[0] ?? {};
        },
        enabled: !!rid && isInitialized && isRidInActiveVersion,
    });
    // data_reporting reports: fetch latest version by key (skip when rid targets a specific version)
    const getReportData = async () => {
        const { data: response } = await webClient.post('/api/data_reporting/dashboards', {
            key: reportId,
        });
        return response.data[0] ?? {};
    };
    const { data: apiDashboard, isLoading, isFetching: isFetchingApiDashboard, isError: isDashboardError, error: dashboardError, } = useQuery({
        queryKey: ['data_reports', studyId, 'reports_list', reportId],
        queryFn: getReportData,
        enabled: isInitialized && (!rid || !isRidInActiveVersion),
    });
    // Analytics: fire dashboard load failed if config fetch errors
    useEffect(() => {
        if (isDashboardError) {
            logReportEvent(ReportAnalyticsEvents.DASHBOARD_LOAD_FAILED, {
                error_type: 'dashboard_config_fetch',
                error_message: dashboardError instanceof Error ? dashboardError.message : 'Unknown error',
            });
        }
    }, [isDashboardError, dashboardError]);
    const rosettaReportMatch = useMemo(() => {
        if (rid || (apiDashboard && Object.keys(apiDashboard).length > 0)) {
            return null;
        }
        return rosettaReports?.find((r) => r.libraryResourceKey === reportId) ?? null;
    }, [rid, apiDashboard, rosettaReports, reportId]);
    const isRosettaReport = !!rosettaReportMatch;
    useEffect(() => {
        // Wait for the BFF dashboard query to fully settle before deciding the rid is
        // unavailable — `isLoading` is false on subsequent fetches (refetch/cache),
        // so without `isFetching` we can fire a redirect on stale empty data.
        if (!rid || isRidInActiveVersion || !isInitialized || isLoading || isFetchingApiDashboard) {
            return;
        }
        if (apiDashboard && Object.keys(apiDashboard).length > 0) {
            return;
        }
        if (!reportsListPath) {
            return;
        }
        navigate(reportsListPath, { replace: true });
        toast.addNotification({
            type: 'error',
            title: translate('This report is not available in the selected environment version.'),
        });
    }, [
        rid,
        isRidInActiveVersion,
        isInitialized,
        isLoading,
        isFetchingApiDashboard,
        apiDashboard,
        reportsListPath,
        navigate,
        toast,
        translate,
    ]);
    const { data: libraryDashboard, isLoading: isLoadingLibrary } = useQuery({
        queryKey: ['data_reports', studyId, 'library_dashboard', rosettaReportMatch?.libraryResourceId],
        queryFn: async () => {
            const { data: response } = await libraryClient.get('/v1/reporting_dashboard', {
                params: { id: rosettaReportMatch.libraryResourceId },
            });
            return response.data[0] ?? {};
        },
        enabled: isRosettaReport && isInitialized,
    });
    const dashboardResource = useMemo(() => {
        if (studyConfigDashboard && Object.keys(studyConfigDashboard).length > 0) {
            return studyConfigDashboard;
        }
        if (apiDashboard && Object.keys(apiDashboard).length > 0) {
            return apiDashboard;
        }
        if (libraryDashboard && Object.keys(libraryDashboard).length > 0) {
            return libraryDashboard;
        }
        return apiDashboard;
    }, [studyConfigDashboard, apiDashboard, libraryDashboard]);
    /**
     * Dashboards coming from the Library carry the builder's grid layout, so their cards can be sized
     * from `ySpan` to match what the builder shows. Reports served by the data_reporting function predate
     * that layout and keep the uniform card height they have always rendered with.
     */
    const isLibraryDashboard = useMemo(() => {
        const hasStudyConfigDashboard = !!studyConfigDashboard && Object.keys(studyConfigDashboard).length > 0;
        const hasApiDashboard = !!apiDashboard && Object.keys(apiDashboard).length > 0;
        const hasLibraryDashboard = !!libraryDashboard && Object.keys(libraryDashboard).length > 0;
        return hasStudyConfigDashboard || (!hasApiDashboard && hasLibraryDashboard);
    }, [studyConfigDashboard, apiDashboard, libraryDashboard]);
    const combinedLoading = isLoading || isLoadingLibrary || isLoadingStudyConfig;
    const dashboardData = dashboardResource?.data;
    // Prefer the per-study custom name stored on the rosetta study-config entry (matched by
    // libraryResourceKey === reportId). Reports that only come from data_reporting have no rosetta
    // entry for this key, so they fall back to the fetched library/data_reporting name.
    const rosettaReportName = useMemo(() => rosettaReports?.find((report) => report.libraryResourceKey === reportId)?.name, [rosettaReports, reportId]);
    /**
     * Library builder sorts charts by (gridY, gridX, position) for display, but the persisted
     * data.charts array order is not kept in sync with the visual layout — saved order reflects
     * checkbox-tick / creation order. Apply the same sort here so the study view matches the
     * builder's visual layout. See SSP-3849.
     */
    const sortedCharts = useMemo(() => (dashboardData?.charts ? sortDashboardChartRefsForGrid(dashboardData.charts) : []), [dashboardData?.charts]);
    // Only non-composite charts report load status via `onChartLoadStatus`, so load/refresh
    // completion must be measured against this count (not the full chart list) — otherwise a
    // dashboard containing composite cards would never settle and the refresh spinner would hang.
    const trackableChartCount = useMemo(() => (dashboardData?.charts ?? []).filter((chart) => chart.type !== 'composite').length, [dashboardData?.charts]);
    /**
     * Only dashboards whose charts were laid out in the builder carry a `ySpan`. Sizing cards from a
     * missing `ySpan` collapses them to a single row track, so a dashboard that never recorded one
     * keeps the uniform card height it rendered with before. Require every chart to have one: a
     * partially laid out dashboard would otherwise squash the charts that don't.
     */
    const hasRowSpanLayout = useMemo(() => sortedCharts.length > 0 && sortedCharts.every((chart) => typeof chart.ySpan === 'number'), [sortedCharts]);
    const chartDescriptionQueries = useQueries({
        queries: (dashboardData?.charts ?? []).map((chart) => ({
            queryKey: ['data_reports', studyId, reportId, 'chart_metadata', chart.id],
            queryFn: async () => {
                const params = chart.libraryResourceId
                    ? { id: chart.libraryResourceId, ...(dashboardResource?.public && { public: true }), ...libRequestsParams }
                    : { key: chart.id };
                const { data: response } = await libraryClient.get('/v1/reporting_chart', {
                    params,
                });
                const resource = response.data[0];
                if (!resource && !chart.libraryResourceId) {
                    const { data: publicResponse } = await libraryClient.get('/v1/reporting_chart', { params: { ...params, public: true } });
                    return { chartId: chart.id, description: publicResponse.data[0]?.metadata?.description };
                }
                return { chartId: chart.id, description: resource?.metadata?.description };
            },
            enabled: isExportModalOpen && !!dashboardData?.charts?.length,
            staleTime: 5 * 60 * 1000,
        })),
    });
    const chartDescriptions = useMemo(() => {
        const map = {};
        for (const query of chartDescriptionQueries) {
            if (query.data?.chartId && query.data?.description) {
                map[query.data.chartId] = query.data.description;
            }
        }
        return map;
    }, [chartDescriptionQueries]);
    // Analytics: check if all charts have settled and fire dashboard load completed
    useEffect(() => {
        if (dashboardLoadCompleteLoggedRef.current || !dashboardData?.charts?.length) {
            return;
        }
        const totalCharts = dashboardData.charts.length;
        const statuses = Object.values(chartStatusMap);
        if (statuses.length < trackableChartCount || statuses.some((s) => s === 'loading')) {
            return;
        }
        dashboardLoadCompleteLoggedRef.current = true;
        const chartsLoaded = statuses.filter((s) => s === 'loaded').length;
        const chartsFailed = statuses.filter((s) => s === 'failed').length;
        const loadDurationMs = Math.round(performance.now() - dashboardLoadStartRef.current);
        logReportEvent(ReportAnalyticsEvents.DASHBOARD_LOAD_COMPLETED, {
            load_duration_ms: loadDurationMs,
            chart_count: totalCharts,
            charts_loaded: chartsLoaded,
            charts_failed: chartsFailed,
            report_name: dashboardData.name,
        });
    }, [chartStatusMap, dashboardData, logReportEvent]);
    // When all charts settle (initial load or refresh), capture lastUpdated from Cube.js lastRefreshTime
    useEffect(() => {
        if (!dashboardData?.charts?.length) {
            return;
        }
        const statuses = Object.values(chartStatusMap);
        if (statuses.length < trackableChartCount || statuses.some((s) => s === 'loading')) {
            return;
        }
        const refreshTimes = Object.values(chartRefreshTimeMap).filter(Boolean);
        if (refreshTimes.length > 0) {
            refreshTimes.sort();
            setLastUpdated(refreshTimes[refreshTimes.length - 1]);
        }
        if (isRefreshing) {
            setIsRefreshing(false);
            setForceDisablePreAggs(false);
        }
    }, [isRefreshing, chartStatusMap, dashboardData?.charts?.length, trackableChartCount, chartRefreshTimeMap]);
    // Pre-apply default filter values from the dashboard config
    const hasSeededDefaultsRef = useRef(false);
    useEffect(() => {
        if (hasSeededDefaultsRef.current || !dashboardData?.filters) {
            return;
        }
        const defaultParams = buildDefaultFilterParams(dashboardData.filters);
        if (defaultParams.length > 0) {
            hasSeededDefaultsRef.current = true;
            setFilterParams(defaultParams);
            logReportEvent(ReportAnalyticsEvents.FILTER_APPLIED, {
                filter_type: 'dashboard_global',
                filters_applied: defaultParams.map((f) => f.member).join(','),
                filter_count: defaultParams.length,
                has_default_value: true,
            });
        }
    }, [dashboardData?.filters, logReportEvent]);
    const resetChartFilterModalParams = () => {
        setChartFilterModal({ id: undefined, isOpen: false, filterType: undefined, filterData: undefined });
    };
    const clearChartFilters = () => {
        setChartQueryFilters((prev) => {
            const updatedFilters = { ...prev };
            delete updatedFilters[chartFilterModal.id];
            return updatedFilters;
        });
        resetChartFilterModalParams();
    };
    const applyQueryChartFilters = ({ selectedOptions, queryKey, }) => {
        setChartQueryFilters((prev) => ({
            ...prev,
            [chartFilterModal.id]: { [queryKey]: selectedOptions },
        }));
        if (selectedOptions.length > 0) {
            logReportEvent(ReportAnalyticsEvents.FILTER_APPLIED, {
                filter_type: 'chart_query',
                filter_key: queryKey,
                chart_id: chartFilterModal.id,
                filter_value_count: selectedOptions.length,
            });
        }
        resetChartFilterModalParams();
        if (selectedOptions.length === 0) {
            clearChartFilters();
        }
    };
    const applyPresetChartFilters = ({ selectedOptions }) => {
        setChartPresetFilters((prev) => ({
            ...prev,
            [chartFilterModal.id]: selectedOptions,
        }));
        if (selectedOptions.length > 0) {
            logReportEvent(ReportAnalyticsEvents.FILTER_APPLIED, {
                filter_type: 'chart_preset',
                chart_id: chartFilterModal.id,
                filter_values: selectedOptions.map((o) => o.option.value).join(','),
                filter_value_count: selectedOptions.length,
            });
        }
        resetChartFilterModalParams();
        if (selectedOptions.length === 0) {
            clearChartFilters();
        }
    };
    const removePaginationLimits = (query) => {
        const { limit: _limit, offset: _offset, total: _total, ...rest } = query;
        return rest;
    };
    const triggerDownload = (blob, fileName) => {
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };
    const getChartDataForExport = async (chartConfig) => {
        const params = chartConfig.libraryResourceId
            ? { id: chartConfig.libraryResourceId, ...(dashboardResource?.public && { public: true }), ...libRequestsParams }
            : { key: chartConfig.id };
        const { data: response } = await libraryClient.get('/v1/reporting_chart', {
            params,
        });
        if (!chartConfig.libraryResourceId && response.data.length === 0) {
            const { data: publicResponse } = await libraryClient.get('/v1/reporting_chart', {
                params: { ...params, public: true },
            });
            return publicResponse.data[0]?.data;
        }
        return response.data[0]?.data;
    };
    const handleDashboardExport = async (selectedChartIds, applyCurrentFilters, keepFormat) => {
        logReportEvent(ReportAnalyticsEvents.EXPORT_STARTED, {
            export_scope: 'dashboard',
            report_name: dashboardData?.name ?? dashboardResource?.name,
            charts_selected: selectedChartIds.length,
            apply_filters: applyCurrentFilters,
            keep_format: keepFormat,
        });
        try {
            setIsExportLoading(true);
            if (!dashboardData?.charts?.length) {
                return;
            }
            const selectedCharts = dashboardData.charts.filter((chart) => selectedChartIds.includes(chart.id));
            if (selectedCharts.length === 0) {
                throw new Error(translate('Select at least one chart to export.'));
            }
            setExportProgress({ processed: 0, total: selectedCharts.length });
            if (activeReportsTab === 0 && !selectedTestEnv.value) {
                throw new Error(translate('Please select a test environment before exporting.'));
            }
            if (!cubeApi) {
                throw new Error(translate('Analytics service is not available.'));
            }
            const timestamp = getExportTimestamp();
            const zip = new JSZip();
            let processedCount = 0;
            for (const chartConfig of selectedCharts) {
                try {
                    const chartData = await getChartDataForExport(chartConfig);
                    if (!chartData?.query) {
                        continue;
                    }
                    const query = applyCurrentFilters
                        ? getQueryParams(chartData.query, filterParams, chartQueryFilters[chartConfig.id], chartPresetFilters[chartConfig.id])
                        : chartData.query;
                    const unboundedQuery = removePaginationLimits(query);
                    const isTableChart = chartData.type === 'table';
                    const cachedNonTableResultSets = nonTableChartResultSetsById[chartConfig.id];
                    const exportResultSets = isTableChart
                        ? (await fetchAllCubePages({
                            cubeApi: cubeApi,
                            query: unboundedQuery,
                            loadOptions: { subscribe: true },
                            limit: EXPORT_PAGE_LIMIT,
                        })).resultSets
                        : applyCurrentFilters && cachedNonTableResultSets && cachedNonTableResultSets.length > 0
                            ? cachedNonTableResultSets
                            : [await cubeApi.load(unboundedQuery, { subscribe: true })];
                    if (exportResultSets.length === 0) {
                        continue;
                    }
                    const transformedData = getTransformedExportData({
                        exportResultSets,
                        chartData,
                        isTableChart,
                        locale,
                        keepFormat,
                        // Legacy reports (no `rid` in URL) export date-only for TIME dimensions
                        // to match on-screen table format; new-flow reports export full timestamps (SSP-4116).
                        showFullTimestamp: !!rid,
                    });
                    const chartName = sanitizeFileName(chartData.title || chartConfig.title || chartConfig.name || 'chart');
                    const csvContent = convertJsonToCsv(transformedData);
                    if (!csvContent) {
                        continue;
                    }
                    zip.file(`${chartName}_${timestamp}.csv`, CSV_BOM + csvContent);
                }
                finally {
                    processedCount += 1;
                    setExportProgress({ processed: processedCount, total: selectedCharts.length });
                }
            }
            if (Object.keys(zip.files).length === 0) {
                throw new Error(translate('No chart data was available to export.'));
            }
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            const dashboardName = sanitizeFileName(dashboardData?.name || dashboardResource?.name || 'dashboard');
            triggerDownload(zipBlob, `${dashboardName}_${timestamp}.zip`);
            logReportEvent(ReportAnalyticsEvents.EXPORT_COMPLETED, {
                export_scope: 'dashboard',
                report_name: dashboardData?.name ?? dashboardResource?.name,
                charts_exported: Object.keys(zip.files).length,
            });
            toast.addNotification({
                type: 'success',
                title: translate('Dashboard export complete'),
            });
            setIsExportModalOpen(false);
        }
        catch (error) {
            const errorMessage = error?.message || error?.error?.message || translate('An error occurred while exporting. Please try again.');
            logReportEvent(ReportAnalyticsEvents.EXPORT_FAILED, {
                export_scope: 'dashboard',
                report_name: dashboardData?.name ?? dashboardResource?.name,
                error_message: errorMessage,
            });
            toast.addNotification({
                type: 'error',
                title: errorMessage,
            });
        }
        finally {
            setIsExportLoading(false);
            setExportProgress({ processed: 0, total: 0 });
        }
    };
    const headerOptions = [
        {
            name: translate('Export'),
            onClick: () => setIsExportModalOpen(true),
            variant: 'primary',
            disabled: combinedLoading || isExportLoading || isRefreshing || !dashboardData?.charts?.length,
            isLoading: isExportLoading || combinedLoading,
        },
    ];
    const reportName = rosettaReportName ?? dashboardData?.name ?? dashboardResource?.name ?? '';
    // Mirror the Dashboards page: a breadcrumb back to the reports list.
    const breadcrumbPages = reportsListPath
        ? [
            { name: translate('Reports'), path: reportsListPath },
            { name: reportName, path: '' },
        ]
        : [];
    return (_jsx(CubeProvider, { cubeApi: cubeApi, children: _jsx(SidePanelLayout, { children: _jsxs(PageWrapper, { className: 'min-w-0', children: [combinedLoading && _jsx(Spinner, { wrapper: 'full' }), _jsx(PageHeader, { breadcrumbPages: breadcrumbPages, title: _jsx(H3, { children: reportName }), subtitle: dashboardData?.description, options: headerOptions, customOptions: _jsx(DashboardRefreshWidget, { lastUpdated: lastUpdated, isRefreshing: isRefreshing, onRefresh: handleRefresh }), LinkComponent: Link }), shouldShowEnvironmentSelector ? (_jsx(TestEnvironmentSelector, { isOpen: shouldShowEnvironmentSelector, onClose: () => setShouldShowEnvironmentSelector(false), onEnvironmentSelected: handleEnvironmentSelected, environmentOptions: rid ? filteredEnvironmentOptions : testEnvironmentsOptions })) : (_jsxs(_Fragment, { children: [!combinedLoading && dashboardData && Object.keys(dashboardData).length === 0 && (_jsx("div", { children: translate('No data found') })), !combinedLoading && dashboardData && sortedCharts.length > 0 && (_jsxs(_Fragment, { children: [_jsx(DataReportGlobalFilters, { isLoading: combinedLoading, dashboardData: dashboardData, studyId: studyId, environmentId: selectedTestEnv.value, cubeApi: cubeApi, analyticsEnv: env, setFilterParams: setFilterParams, logReportEvent: logReportEvent, disabled: isRefreshing, layout: 'panel' }), _jsx(ReportingGrid, { padding: 4, cards: sortedCharts.map((chart) => {
                                            return {
                                                card: (_jsx(ChartCard, { chart: chart, chartQueryFilters: chartQueryFilters, chartPresetFilters: chartPresetFilters, setChartFilterModal: setChartFilterModal, filterParams: filterParams, studyId: studyId, reportId: reportId, isPublic: dashboardResource.public, onNonTableResultSetsChange: handleNonTableResultSetsChange, onChartLoadStatus: handleChartLoadStatus, onLastRefreshTime: handleChartLastRefreshTime, logReportEvent: logReportEvent, forceDisablePreAggs: forceDisablePreAggs, refreshNonce: refreshNonce, interactionsDisabled: isRefreshing })),
                                                xSpan: chart.xSpan,
                                                ySpan: chart.ySpan,
                                            };
                                        }), rowHeight: 'base', scaleHeightToRowSpan: isLibraryDashboard && hasRowSpanLayout })] }))] })), chartFilterModal.isOpen && (_jsxs(_Fragment, { children: [chartFilterModal.filterType === 'query' && (_jsx(ChartFilterModal, { isOpen: chartFilterModal.isOpen, filterData: chartFilterModal.filterData, onClose: resetChartFilterModalParams, applyFilters: applyQueryChartFilters, initialSelectedOptions: Object.values(chartQueryFilters[chartFilterModal.id] || {}).flat(), logReportEvent: logReportEvent })), chartFilterModal.filterType === 'preset' && (_jsx(ChartFilterModal, { isOpen: chartFilterModal.isOpen, filterData: chartFilterModal.filterData, onClose: resetChartFilterModalParams, applyFilters: applyPresetChartFilters, initialSelectedOptions: (chartPresetFilters[chartFilterModal.id] || []).map((filter) => filter.option), logReportEvent: logReportEvent }))] })), _jsx(ExportReportModal, { isOpen: isExportModalOpen, onClose: () => setIsExportModalOpen(false), charts: dashboardData?.charts || [], chartDescriptions: chartDescriptions, dashboardName: dashboardData?.name ?? dashboardResource?.name, isExporting: isExportLoading, exportProgress: exportProgress, onExport: handleDashboardExport, allowFormatting: applyValueFormatting })] }) }) }));
};
